import { Box, Flex, Text } from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import * as _ from 'lodash';
import React, { useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Date as DateType } from '@finley-technologies/finley-types';

import {
  EditRecurringDateModal,
  EditRelativeRecurringDateModal,
} from './EditRecurringDateModal';
import { colors } from 'packages/common-frontend/colors';
import { EditIconButton } from 'packages/common-frontend/components/IconButton';
import {
  convertRecurringDateConfigsToConfigFormStates,
  generateHumanReadableString,
  isBaseRecurringDateConfig,
  convertRelativeRecurringDateConfigsToConfigFormStates,
} from 'packages/common-frontend/components/RecurringDateForm/utils';
import { WipConfig } from 'packages/creditAgreements/ui/types';

type RecurringDateConfigViewProps = {
  recurringDateConfig: DateType.RecurringDate.Config;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  displayName?: string;
  displayStyle?: 'editor' | 'humanReadableString';
  enableEdit: boolean;
};
export const RecurringDateConfigView = ({
  recurringDateConfig,
  wipConfig,
  fullPropertyKey,
  displayName = 'Recurring Date',
  displayStyle = 'editor',
  enableEdit,
}: RecurringDateConfigViewProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const onSave = (config: DateType.RecurringDate.Config) => {
    if (wipConfig) {
      wipConfig.formProps.setFieldValue(fullPropertyKey, config);
    }
    setIsOpen(false);
  };

  const { recurringDateConfigs } = recurringDateConfig;
  const savedRecurringDateConfigs = recurringDateConfigs.filter(
    isBaseRecurringDateConfig,
  );
  const allRecurring =
    savedRecurringDateConfigs.length > 0
      ? convertRecurringDateConfigsToConfigFormStates(savedRecurringDateConfigs)
      : [{ isValid: false, isRelative: false, touched: false }];

  const humanReadableStrings = _.compact(
    _.map(allRecurring, (config) => {
      const {
        startDate,
        endDate,
        period,
        cadence,
        offsetDays,
        offsetDayUnit,
      } = config;

      const startDateString = startDate
        ? commonDate.dateToHumanDateString(startDate)
        : commonDate.currentDateStr();
      const endDateString = endDate
        ? commonDate.dateToHumanDateString(endDate)
        : commonDate.currentDateStr();

      return generateHumanReadableString({
        ...config,
        cadence: cadence ?? 1,
        offsetDayUnit: offsetDayUnit ?? 'calendarDay',
        offsetDays: offsetDays ?? 0,
        period: period ?? 'calendarDate',
        startDate: startDateString,
        endDate: endDateString,
        startDateNumber: startDate ? startDate.getDate() : undefined,
      });
    }),
  );

  if (
    displayStyle === 'humanReadableString' &&
    Boolean(humanReadableStrings.length)
  ) {
    return (
      <Box>
        <Flex alignItems="center" mb="3">
          <Box
            p="8px 16px"
            borderRadius="8px"
            backgroundColor={colors.gray[200]}
            width="100%"
            mr="24px"
          >
            {_.map(humanReadableStrings, (readableString) => {
              return (
                <Text
                  key={readableString}
                  color={colors.black[95]}
                  fontWeight={500}
                  lineHeight="24px"
                >
                  {readableString}
                </Text>
              );
            })}
          </Box>
          {enableEdit && wipConfig?.isMutateAllowed(fullPropertyKey) && (
            <>
              <EditIconButton
                ariaLabel="edit"
                label="Edit"
                onClick={() => {
                  setIsOpen(true);
                }}
              />
              <EditRecurringDateModal
                isOpen={isOpen}
                onCancel={() => setIsOpen(false)}
                onSave={onSave}
                recurringDateConfig={recurringDateConfig}
                displayName={displayName}
              />
            </>
          )}
        </Flex>
      </Box>
    );
  }

  return (
    <Box mb="6">
      <Flex alignItems="center" mb="3">
        <Text fontWeight="bold" fontSize="large" mr="2">
          {displayName}
        </Text>
        {enableEdit && wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <EditIconButton
              ariaLabel="edit"
              label="Edit"
              onClick={() => {
                setIsOpen(true);
              }}
            />
            <EditRecurringDateModal
              isOpen={isOpen}
              onCancel={() => setIsOpen(false)}
              onSave={onSave}
              recurringDateConfig={recurringDateConfig}
              displayName={displayName}
            />
          </>
        )}
      </Flex>

      <Editor
        defaultLanguage="json"
        value={JSON.stringify(recurringDateConfig, null, 4)}
        theme="vs-dark"
        height="15vh"
        width="1200px"
        options={{
          readOnly: true,
          fixedOverflowWidgets: true,
          minimap: { enabled: false },
        }}
      />
    </Box>
  );
};

export const RelativeRecurringDateConfigView: React.FC<{
  relativeConfig: DateType.RecurringDate.RelativeConfig;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  displayName?: string;
  displayStyle?: 'editor' | 'humanReadableString';
  enableEdit?: boolean;
}> = ({
  relativeConfig,
  wipConfig,
  fullPropertyKey,
  displayName = 'Recurring Date',
  displayStyle = 'editor',
  enableEdit = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const onSave = (config: DateType.RecurringDate.Config) => {
    if (wipConfig) {
      wipConfig.formProps.setFieldValue(fullPropertyKey, config);
    }
    setIsOpen(false);
  };

  const { relativeRecurringDateConfig } = relativeConfig;
  const allRecurring =
    relativeRecurringDateConfig.length > 0
      ? convertRelativeRecurringDateConfigsToConfigFormStates(
          relativeRecurringDateConfig,
        )
      : [{ isValid: false, isRelative: false, touched: false }];

  const humanReadableStrings = _.compact(
    _.map(allRecurring, (config) => {
      const {
        period,
        cadence,
        offsetDays,
        offsetDayUnit,
        frequency,
        selectedDays,
        occurrence,
        startDateFormula,
        endDateFormula,
      } = config;

      return generateHumanReadableString({
        ...config,
        cadence: cadence ?? 1,
        offsetDayUnit: offsetDayUnit ?? 'calendarDay',
        offsetDays: offsetDays ?? 0,
        period: period ?? 'calendarDate',
        startDate: startDateFormula,
        startDateNumber: undefined,
        endDate: endDateFormula,
        frequency,
        selectedDays,
        occurrence,
      });
    }),
  );

  if (
    displayStyle === 'humanReadableString' &&
    Boolean(humanReadableStrings.length)
  ) {
    return (
      <Box>
        <Flex alignItems="center" mb="3">
          <Box
            p="8px 16px"
            borderRadius="8px"
            backgroundColor={colors.gray[200]}
            width="100%"
            mr="24px"
          >
            {_.map(humanReadableStrings, (readableString) => {
              return (
                <Text
                  key={readableString}
                  color={colors.black[95]}
                  fontWeight={500}
                  lineHeight="24px"
                >
                  {readableString}
                </Text>
              );
            })}
          </Box>
          {enableEdit && wipConfig?.isMutateAllowed(fullPropertyKey) && (
            <>
              <EditIconButton
                ariaLabel="edit"
                label="Edit"
                onClick={() => {
                  setIsOpen(true);
                }}
              />
              <EditRelativeRecurringDateModal
                isOpen={isOpen}
                onCancel={() => setIsOpen(false)}
                onSave={onSave}
                relativeDateConfig={relativeConfig}
                displayName={displayName}
              />
            </>
          )}
        </Flex>
      </Box>
    );
  }

  return (
    <Box mb="6">
      <Flex alignItems="center" mb="3">
        <Text fontWeight="bold" fontSize="large" mr="2">
          {displayName}
        </Text>
        {enableEdit && wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <EditIconButton
              ariaLabel="edit"
              label="Edit"
              onClick={() => {
                setIsOpen(true);
              }}
            />
            <EditRelativeRecurringDateModal
              isOpen={isOpen}
              onCancel={() => setIsOpen(false)}
              onSave={onSave}
              relativeDateConfig={relativeConfig}
              displayName={displayName}
            />
          </>
        )}
      </Flex>

      <Editor
        defaultLanguage="json"
        value={JSON.stringify(relativeConfig, null, 4)}
        theme="vs-dark"
        height="15vh"
        width="1200px"
        options={{
          readOnly: true,
          fixedOverflowWidgets: true,
          minimap: { enabled: false },
        }}
      />
    </Box>
  );
};
